/**
 * The shape of the plan (P04, Q-4): the main line and the reserve beats that hang under their beat.
 * One place for «which beats are numbered», «what is next» and «where a reserve beat is drawn», so
 * the Studio rail, the beat overview and the table's «План» never disagree.
 */

import type { BeatStatus } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';

/** The words of a beat's status (one place; the rail, the beat's header and the table's plan use it). */
export const BEAT_STATUS_KEY: Readonly<Record<BeatStatus, keyof TranslationKeys>> = {
  draft: 'studio.beat.status.draft',
  ready: 'studio.beat.status.ready',
  played: 'studio.beat.status.played',
  carried: 'studio.beat.status.carried',
};

export interface PlanOutcome {
  readonly id: string;
  readonly label: string;
  readonly toBeatId: string;
}

export interface PlanBeatLike {
  readonly id: string;
  readonly order: number;
  readonly reserveOf?: string | null;
  readonly outcomes?: readonly PlanOutcome[];
}

const byOrder = (a: { readonly order: number }, b: { readonly order: number }): number => a.order - b.order;

/** A reserve beat is a beat of another beat, outside the main line. */
export const isReserve = (beat: PlanBeatLike): boolean => beat.reserveOf != null;

/** The main line: beats that are not a reserve, in the plan's order. */
export function mainLine<B extends PlanBeatLike>(beats: readonly B[]): B[] {
  return beats.filter((b) => !isReserve(b)).sort(byOrder);
}

/** The number of a beat: its place in the main line, from 1. A reserve beat has none. */
export function beatNumber(beats: readonly PlanBeatLike[], beatId: string): number | null {
  const at = mainLine(beats).findIndex((b) => b.id === beatId);
  return at < 0 ? null : at + 1;
}

export interface PlanRow<B extends PlanBeatLike> {
  readonly beat: B;
  /** Position in the main line; null for a reserve beat. */
  readonly number: number | null;
  /** For a reserve beat: the label of the fork that leads to it (as the GM typed it), when there is one. */
  readonly forkLabel?: string;
}

/**
 * The plan top to bottom: each main-line beat, and right under it its reserve beats (those that
 * the beat's forks lead to first, in the order of the forks, then any other reserve of that beat).
 * A reserve beat whose beat is gone is not lost: it closes the list, un-numbered.
 */
export function planRows<B extends PlanBeatLike>(beats: readonly B[]): PlanRow<B>[] {
  const main = mainLine(beats);
  const reserves = beats.filter(isReserve).sort(byOrder);
  const rows: PlanRow<B>[] = [];
  const placed = new Set<string>();
  // A reserve of a reserve hangs under its own parent (not at the end of the list); the walk is guarded against a cycle.
  const hang = (beat: B): void => {
    const mine = reserves.filter((r) => r.reserveOf === beat.id);
    const ordered = [
      ...(beat.outcomes ?? []).map((o) => mine.find((r) => r.id === o.toBeatId)).filter((r): r is B => r !== undefined),
      ...mine,
    ].filter((r, i, all) => all.indexOf(r) === i);
    for (const reserve of ordered) {
      if (placed.has(reserve.id)) continue;
      const fork = (beat.outcomes ?? []).find((o) => o.toBeatId === reserve.id);
      rows.push({ beat: reserve, number: null, ...(fork !== undefined ? { forkLabel: fork.label } : {}) });
      placed.add(reserve.id);
      hang(reserve);
    }
  };
  main.forEach((beat, index) => {
    rows.push({ beat, number: index + 1 });
    hang(beat);
  });
  for (const reserve of reserves) {
    if (placed.has(reserve.id)) continue;
    // Its beat is not in the main line (itself a reserve, or gone): still shown, never dropped.
    rows.push({ beat: reserve, number: null });
  }
  return rows;
}

/**
 * «Следующий»: the next beat of the main line after the beat; for a reserve beat — the one after
 * the beat it forks from. null at the end of the plan.
 */
export function nextInPlan<B extends PlanBeatLike>(beats: readonly B[], beatId: string): B | null {
  const main = mainLine(beats);
  const beat = beats.find((b) => b.id === beatId);
  if (beat === undefined) return null;
  let anchor: PlanBeatLike | undefined = beat;
  // Walk up from a reserve beat to the main-line beat it hangs from (a cycle cannot loop: the walk is bounded).
  for (let guard = 0; anchor !== undefined && isReserve(anchor) && guard < beats.length; guard++) {
    const parentId: string = anchor.reserveOf as string;
    anchor = beats.find((b) => b.id === parentId);
  }
  if (anchor === undefined) return null;
  const at = main.findIndex((b) => b.id === anchor!.id);
  return at < 0 ? null : (main[at + 1] ?? null);
}

/** «Если проиграли»: the label of a fork as it is displayed — lower case; stored as typed. */
export const forkWords = (label: string): string => label.toLowerCase();
